/**
 * Performance Instrumentation — observable counters and canaries for the
 * audit findings documented in docs/frontend-performance-audit.md.
 *
 * Design goals:
 *  - Zero runtime cost in production (IS_PROD early-return in every function).
 *  - All counters exposed on window.__riverPerf so you can query from DevTools:
 *      window.__riverPerf.report()
 *      window.__riverPerf.assertInvariants()
 *  - Purely additive — does not change any existing code path.
 *  - Each probe maps to a numbered audit finding so it is easy to cross-reference.
 *
 * Usage from any file:
 *   import { perf } from '../utils/perfInstrumentation';
 *   perf.increment('systemStoreInitCount');
 */

import { IS_PROD } from './devMode';

// ─── Counter names (one per audit finding + a few structural canaries) ────────

export type PerfCounter =
  // Finding #2 — systemStore init fires on navigation
  | 'systemStoreInitCount'          // increments each time initSystemStore() runs
  | 'systemStoreInitWithUser'       // increments only when user+authToken present

  // Finding #4 — userTrackingService.init() double-call
  | 'userTrackingInitCount'         // increments on every userTrackingService.init() call

  // Finding #5 — initializeAnalytics double-fires
  | 'analyticsInitCount'            // increments each time initializeAnalytics() is called

  // Finding #1 — MCP tools skipped because entities not loaded
  | 'mcpInitSkippedEmptyEntities'   // increments when MCP init returns early (entities=[])
  | 'mcpInitRan'                    // increments when MCP init proceeds past the empty guard
  | 'mcpToolsRegistered'            // total tool commands registered across all connections

  // Finding #6 — ToastContainers re-render count
  | 'toastContainerReRenders'       // increments on every ToastContainer setToasts() call

  // Finding #8 — notification subscription leak
  | 'notifSubscribeCount'           // increments on every subscribeNotificationsYjs() call
  | 'notifUnsubscribeCount'         // increments on every unsubscribe call

  // Finding #11 — defaults-status calls
  | 'defaultsStatusCheckCount'      // increments each GET /user/defaults-status call

  // Canary — context divergence between systemStore and dataspaceContextStore
  | 'contextDivergenceDetected';    // increments when the two context.orgId values differ


// ─── Snapshot type ───────────────────────────────────────────────────────────

interface PerfSnapshot {
  counters: Record<PerfCounter, number>;
  ts: number;
}

// ─── State ───────────────────────────────────────────────────────────────────

const _counters: Record<string, number> = {};
const _snapshots: PerfSnapshot[] = [];
const _violations: Array<{ counter: PerfCounter; threshold: number; actual: number; ts: number }> = [];

// ─── Thresholds: what counts as "degraded" in a single session ───────────────
// These are the upper bounds that should hold after all audit fixes are applied.
// They are deliberately generous to avoid false positives in unusual (but valid)
// sessions. The important invariants are marked with ⚠️.
const _thresholds: Partial<Record<PerfCounter, number>> = {
  systemStoreInitCount: 2,           // ⚠️ once on mount, at most once on user change
  userTrackingInitCount: 1,          // ⚠️ should be called exactly once
  analyticsInitCount: 1,             // ⚠️ should be called exactly once
  mcpInitSkippedEmptyEntities: 0,    // ⚠️ zero skips after fix — entities must be ready
  notifSubscribeCount: 1,            // ⚠️ one subscription per user session
  contextDivergenceDetected: 0,      // ⚠️ always zero after dual-write fix
  defaultsStatusCheckCount: 1,       // one per session; profile changes should not re-trigger
};

// ─── Public API ──────────────────────────────────────────────────────────────

export const perf = {
  increment(counter: PerfCounter, by = 1): void {
    if (IS_PROD) return;
    _counters[counter] = (_counters[counter] ?? 0) + by;
    _checkThreshold(counter);
  },

  get(counter: PerfCounter): number {
    return _counters[counter] ?? 0;
  },

  snapshot(label?: string): PerfSnapshot {
    const snap: PerfSnapshot = {
      counters: { ...(_counters as Record<PerfCounter, number>) },
      ts: Date.now(),
    };
    _snapshots.push(snap);
    if (label) {
      console.groupCollapsed(`[RiverPerf] snapshot: ${label}`);
      console.table(snap.counters);
      console.groupEnd();
    }
    return snap;
  },

  report(): void {
    if (IS_PROD) return;
    console.groupCollapsed('[RiverPerf] Full Counter Report');
    console.table(_counters);
    if (_violations.length > 0) {
      console.warn('[RiverPerf] ⚠️ Threshold violations:', _violations);
    } else {
      console.info('[RiverPerf] ✅ All thresholds within bounds');
    }
    console.groupEnd();
  },

  violations(): typeof _violations {
    return [..._violations];
  },

  assertInvariants(): boolean {
    if (IS_PROD) return true;
    const failing = _violations.filter(v => v.actual > (v.threshold ?? Infinity));
    if (failing.length === 0) {
      console.info('[RiverPerf] ✅ assertInvariants: all invariants hold');
      return true;
    }
    console.error('[RiverPerf] ❌ assertInvariants: violations detected', failing);
    return false;
  },

  /** Reset all counters — useful at the start of a manual test sequence */
  reset(): void {
    if (IS_PROD) return;
    for (const k of Object.keys(_counters)) delete _counters[k];
    _violations.length = 0;
    _snapshots.length = 0;
    console.info('[RiverPerf] ♻️ Counters reset');
  },
};

function _checkThreshold(counter: PerfCounter): void {
  const threshold = _thresholds[counter];
  if (threshold === undefined) return;
  const actual = _counters[counter] ?? 0;
  if (actual > threshold) {
    const violation = { counter, threshold, actual, ts: Date.now() };
    // Only push once per crossing to avoid noise
    const alreadyRecorded = _violations.some(v => v.counter === counter && v.actual === actual);
    if (!alreadyRecorded) {
      _violations.push(violation);
      console.warn(
        `[RiverPerf] ⚠️ THRESHOLD EXCEEDED: ${counter} = ${actual} (max ${threshold})`
      );
    }
  }
}

// ─── Expose on window for DevTools access ────────────────────────────────────

if (!IS_PROD && typeof window !== 'undefined') {
  (window as any).__riverPerf = perf;
  console.info(
    '[RiverPerf] 📊 Instrumentation active. Use window.__riverPerf.report() / ' +
    'window.__riverPerf.assertInvariants() / window.__riverPerf.reset()'
  );
}
